/**
 * Customer detail > Payment methods (tracker item 38): cards on file (brand,
 * last 4, expiry only), add a card through the processor form, set the
 * default, autopay on/off (turning it on records the customer consent the
 * staff member confirms, unless one is already on record), remove.
 * ADMIN and OFFICE_MANAGER (the page hides it from other roles).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, IOnlinePaymentsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { rowActionClass, rowDangerActionClass, secondaryButtonClass, primaryButtonClass } from '../../components/formStyles';
import { CardEntryForm } from './CardEntryForm';
import { cardLabel, expiryLabel } from './onlinePaymentsUi';
import { Toast } from '../../components/Toast';

interface Props {
  customerId: string;
  customerName?: string;
}

export function CustomerPaymentMethodsCard({ customerId, customerName }: Props) {
  const api = useMemo<IOnlinePaymentsApi>(() => createAPIClient(), []);
  const [methods, setMethods] = useState<types.CustomerPaymentMethod[]>([]);
  const [connection, setConnection] = useState<types.ProcessorConnection | null>(null);
  const [consent, setConsent] = useState<{ version: string; text: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [addAutopay, setAddAutopay] = useState(false);
  const [consentFor, setConsentFor] = useState<string | null>(null);
  const [removeFor, setRemoveFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, conn, settings] = await Promise.all([
        api.listPaymentMethods(customerId),
        api.getProcessorConnection().catch(() => null),
        api.getOnlinePaymentSettings().catch(() => null),
      ]);
      setMethods(list);
      setConnection(conn);
      setConsent(settings?.autopayConsent ?? null);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the cards on file'));
    } finally {
      setLoaded(true);
    }
  }, [api, customerId]);

  useEffect(() => { void load(); }, [load]);

  const consentInput = (): types.AutopayConsentInput | undefined => (consent ? { accepted: true, version: consent.version } : undefined);

  async function run(work: () => Promise<unknown>, failure: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, failure));
    } finally {
      setBusy(false);
    }
  }

  const toggleAutopay = (m: types.CustomerPaymentMethod) => {
    if (!m.autopayEnabled && !m.autopayConsentAt) {
      setConsentFor(m.id);
      return;
    }
    void run(() => api.setPaymentMethodAutopay(customerId, m.id, { enabled: !m.autopayEnabled }), 'Could not change autopay');
  };

  const canAdd = !!connection?.clientConfig;

  return (
    <section aria-labelledby="payment-methods-heading" className="bg-white rounded-lg shadow p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 id="payment-methods-heading" className="text-lg font-semibold">Payment methods</h2>
        {canAdd && !adding && (
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setAdding(true); setAddAutopay(false); }}>
            Add card
          </button>
        )}
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {!loaded ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : methods.length === 0 && !adding ? (
        <p className="text-sm text-gray-600">
          No cards on file.{!canAdd && ' Connect a card processor in Settings > Payments to add one.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {methods.map((m) => (
            <li key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="payment-method">
              <div className="flex-1 min-w-0">
                <p className="font-medium">
                  {cardLabel(m)}
                  {m.isDefault && <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Default</span>}
                  {m.autopayEnabled && <span className="ml-2 rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">Autopay</span>}
                  {m.status !== 'ACTIVE' && <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{m.status === 'EXPIRED' ? 'Expired' : m.status}</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {expiryLabel(m) && `Expires ${expiryLabel(m)}`}
                  {m.autopayConsentAt && ` · Autopay consent ${m.autopayConsentAt.slice(0, 10)} (${m.autopayConsentBy === 'CUSTOMER' ? 'by the customer' : 'taken by staff'})`}
                </p>
              </div>
              {m.status === 'ACTIVE' && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {!m.isDefault && <button type="button" className={rowActionClass} disabled={busy} onClick={() => void run(() => api.setDefaultPaymentMethod(customerId, m.id), 'Could not set the default card')}>Make default</button>}
                  <button type="button" className={rowActionClass} disabled={busy} onClick={() => toggleAutopay(m)}>
                    {m.autopayEnabled ? 'Turn autopay off' : 'Turn autopay on'}
                  </button>
                  <button type="button" className={rowDangerActionClass} disabled={busy} onClick={() => setRemoveFor(m.id)}>Remove</button>
                </div>
              )}
              {consentFor === m.id && (
                <div role="alertdialog" aria-label="Autopay consent" className="w-full rounded border border-blue-200 bg-blue-50 p-3 text-sm space-y-2">
                  <p className="font-medium">Read this to {customerName ?? 'the customer'} and confirm they agree:</p>
                  <blockquote className="border-l-4 border-blue-300 pl-3 text-gray-800">{consent?.text ?? 'Consent wording is not available.'}</blockquote>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" className={primaryButtonClass} disabled={busy || !consent}
                      onClick={() => void run(async () => {
                        await api.setPaymentMethodAutopay(customerId, m.id, { enabled: true, autopayConsent: consentInput() });
                        setConsentFor(null);
                      }, 'Could not turn autopay on')}>
                      The customer agrees: turn autopay on
                    </button>
                    <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConsentFor(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {removeFor === m.id && (
                <div role="alertdialog" aria-label="Remove card" className="w-full rounded border border-red-200 bg-red-50 p-3 text-sm space-y-2">
                  <p>Remove {cardLabel(m)}? It is deleted at the card processor{m.autopayEnabled ? ' and autopay stops' : ''}.</p>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400" disabled={busy}
                      onClick={() => void run(async () => { await api.removePaymentMethod(customerId, m.id); setRemoveFor(null); }, 'Could not remove the card')}>
                      Remove card
                    </button>
                    <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setRemoveFor(null)}>Keep</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && connection?.clientConfig && (
        <div className="mt-4 rounded border border-gray-200 p-4">
          <h3 className="font-medium mb-2">Add a card</h3>
          <CardEntryForm
            clientConfig={connection.clientConfig}
            submitLabel="Save card"
            onCancel={() => setAdding(false)}
            onToken={async (sourceToken) => {
              await api.addPaymentMethod(customerId, { sourceId: sourceToken, autopayConsent: addAutopay ? consentInput() : undefined });
              setAdding(false);
              await load();
            }}
          >
            {consent && (
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" className="mt-0.5 h-5 w-5" checked={addAutopay} onChange={(e) => setAddAutopay(e.target.checked)} />
                <span>
                  <span className="font-medium">Turn autopay on (the customer agrees to this wording)</span>
                  <span className="block text-xs text-gray-600">{consent.text}</span>
                </span>
              </label>
            )}
          </CardEntryForm>
        </div>
      )}
    </section>
  );
}
